/*----------------------------------------------------------------
  Common filter definitions and SQL composer used by every table view.
----------------------------------------------------------------*/

/** Operator catalogue (shown in the UI) */
export const OPS = [
  { label: "=",           sql: "="   },
  { label: "≠",           sql: "!="  },
  { label: "<",           sql: "<"   },
  { label: "≤",           sql: "<="  },
  { label: ">",           sql: ">"   },
  { label: "≥",           sql: ">="  },
  { label: "BETWEEN",     sql: "between" },
  { label: "IN (…) ",     sql: "in"  },
  { label: "contains",    sql: "contains" },
  { label: "startsWith",  sql: "starts"   },
  { label: "IS NULL",     sql: "isnull"   },
  { label: "IS NOT NULL", sql: "notnull"  },
] as const;

export type OpKey = typeof OPS[number]["sql"];

export interface Filter {
  id: number;
  column: string;
  op: OpKey;
  value: string;          // empty for IS NULL / NOT NULL
}

/*----------------------------------------------------------------
  Build WHERE-clause
  joinOp = 'AND' | 'OR'   (global for now; nesting will come later)
----------------------------------------------------------------*/
export function buildWhereClause(
  applied: Filter[],
  joinOp: "AND" | "OR" = "AND",
): string {
  if (!applied.length) return "";

  const fragments = applied
    .filter((f) => f.column && (f.value.trim() || f.op === "isnull" || f.op === "notnull"))
    .map((f) => {
      const v = f.value.replace(/'/g, "''");   // escape quotes once
      switch (f.op) {
        case "contains":
          return `CAST("${f.column}" AS TEXT) ILIKE '%${v}%'`;
        case "starts":
          return `CAST("${f.column}" AS TEXT) ILIKE '${v}%'`;
        case "between": {
          const [lo, hi] = v.split(/\.{2,}|,/).map((s) => s.trim());
          if (!lo || !hi) return "1=1";        // fail-safe
          const loLit = /^\d/.test(lo) ? lo : `'${lo}'`;
          const hiLit = /^\d/.test(hi) ? hi : `'${hi}'`;
          return `"${f.column}" BETWEEN ${loLit} AND ${hiLit}`;
        }
        case "in": {
          const parts = v.split(",").map((s) => s.trim()).filter(Boolean);
          if (!parts.length) return "1=1";
          const list = parts
            .map((p) =>
              /^\d+(\.\d+)?$/.test(p) || p.toLowerCase() === "null"
                ? p
                : `'${p}'`,
            )
            .join(", ");
          return `"${f.column}" IN (${list})`;
        }
        case "isnull":
          return `"${f.column}" IS NULL`;
        case "notnull":
          return `"${f.column}" IS NOT NULL`;
        default: {
          const lit =
            /^\d+(\.\d+)?$/.test(v) || v.toLowerCase() === "null"
              ? v
              : `'${v}'`;
          return `"${f.column}" ${f.op} ${lit}`;
        }
      }
    });

  return fragments.length ? `WHERE ${fragments.join(` ${joinOp} `)}` : "";
}
